import { useEffect, useMemo, useState } from "react";
import { PhoneIncoming, PhoneOutgoing, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { RedemptionCrm } from "@/components/corto/RedemptionCrm";
import { ChiamateIngresso } from "@/components/corto/ChiamateIngresso";
import { BoxPeriodo, IstogrammaMensile, Legenda, TabellaFasce, TooltipRighe, meseBreve, pct, useMeseSelezionato }
  from "@/components/corto/IstogrammaTelefonate";
import { MESI, dateIt, num } from "@/lib/format";

const inputCls = "w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0018A8]/30";
const VUOTO = { mese: "", dal: "", al: "", agenzia: "", operatore: "", solo_wave: "" };
const C = { effettuate: "#0018A8", risposte: "#10B981", nonRisposte: "#DC2626", senzaEsito: "#CBD5E1",
  richRisposta: "#34D399", richiamanti: "#0EA5E9", richSenza: "#F59E0B", mai: "#7F1D1D" };
const SERIE_PERSE = [["richiamati_risposta", "Richiamati con risposta", C.richRisposta],
  ["richiamanti_risposto", "Richiamanti/risposti", C.richiamanti],
  ["richiamati_senza_risposta", "Richiamati senza risposta", C.richSenza],
  ["mai_risentiti", "Mai più sentiti", C.mai]];
const meseEsteso = (m) => `${MESI[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}`;

function Filtri({ filtri, set, azzera, agenzie, operatori }) {
  const attivi = Object.entries(filtri).some(([k, v]) => k !== "solo_wave" && v);
  return (
    <Card className="p-4" data-testid="telefonate-filtri">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Mese</label>
          <select className={inputCls} value={filtri.mese} data-testid="filtro-telefonate-mese" onChange={(ev) => set("mese", ev.target.value)}>
            <option value="">Tutti</option>
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div className="w-36">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Dal</label>
          <input type="date" className={inputCls} value={filtri.dal} data-testid="filtro-telefonate-dal" onChange={(ev) => set("dal", ev.target.value)} />
        </div>
        <div className="w-36">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Al</label>
          <input type="date" className={inputCls} value={filtri.al} data-testid="filtro-telefonate-al" onChange={(ev) => set("al", ev.target.value)} />
        </div>
        <div className="w-48">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Agenzia</label>
          <select className={inputCls} value={filtri.agenzia} data-testid="filtro-telefonate-agenzia" onChange={(ev) => set("agenzia", ev.target.value)}>
            <option value="">Totale DBE</option>
            {agenzie.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="w-60">
          <label className="text-[11px] uppercase tracking-wider text-slate-400 block mb-1">Operatore (solo uscita)</label>
          <select className={inputCls} value={filtri.operatore} data-testid="filtro-telefonate-operatore" onChange={(ev) => set("operatore", ev.target.value)}>
            <option value="">Tutti</option>
            {operatori.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        {attivi && (
          <button type="button" onClick={azzera} data-testid="filtro-telefonate-azzera"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Azzera filtri
          </button>
        )}
      </div>
    </Card>
  );
}

function SezioneUscita({ uscita, wave, soloWave, setSoloWave }) {
  const [mese, toggle] = useMeseSelezionato();
  const t = uscita.totale;
  const w = wave.totale;
  const dettaglio = mese && uscita.mesi.find((m) => m.nome === mese);
  const perMese = Object.fromEntries(wave.mesi.map((m) => [m.nome, m]));
  const tooltip = ({ active, label, payload }) => {
    const p = payload?.[0]?.payload;
    const wm = perMese[label] || {};
    return p && <TooltipRighe active={active} label={label} righe={[
      [C.effettuate, soloWave ? "Effettuate a clienti in wave" : "Effettuate", p.effettuate],
      [C.risposte, "Risposte", p.risposte, `${pct(p.perc_risposte)} delle effettuate`],
      ["#64748B", "Clienti unici in wave", wm.clienti_wave],
      ["#64748B", "Clienti wave chiamati", wm.chiamati, `${pct(wm.perc_chiamati)} della wave`],
      ["#64748B", "Clienti wave raggiunti", wm.raggiunti, `${pct(wm.perc_raggiunti)} della wave`]]} />;
  };
  const btn = (attivo) => `px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200 ${attivo ? "bg-[#0018A8] text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <section className="space-y-4" data-testid="telefonate-uscita">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
          <PhoneOutgoing size={18} className="text-[#0018A8]" /> Chiamate in uscita
        </h2>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="uscita-toggle-wave">
          <button type="button" className={btn(!soloWave)} onClick={() => setSoloWave(false)} data-testid="uscita-tutte">Tutte le chiamate</button>
          <button type="button" className={btn(soloWave)} onClick={() => setSoloWave(true)} data-testid="uscita-solo-wave">Solo clienti in wave del mese</button>
        </div>
      </div>
      <div className="space-y-4">
        <Card className="p-4 space-y-3">
          <Legenda testId="uscita-legenda" voci={[[C.effettuate, "Chiamate effettuate"], [C.risposte, "Chiamate con risposta"]]} />
          <IstogrammaMensile testId="uscita-istogramma" dati={uscita.mesi} meseSelezionato={mese} onMese={toggle} tooltip={tooltip}
            etichetta="Clicca una barra per vedere le fasce orarie del mese"
            serie={[{ key: "effettuate", nome: "Effettuate", colore: C.effettuate, radius: 3, etichetta: true },
              { key: "risposte", nome: "Risposte", colore: C.risposte, radius: 3, etichetta: true }]} />
        </Card>
        <BoxPeriodo testId="uscita-box" titolo={soloWave ? "Totale periodo · solo clienti in wave del mese" : "Totale periodo"} voci={[
          { key: "effettuate", label: "Chiamate effettuate", valore: t.effettuate, colore: C.effettuate },
          { key: "risposte", label: "Chiamate con risposta", valore: t.risposte, colore: C.risposte },
          { key: "perc", label: "% chiamate risposte", valore: null, perc: t.perc_risposte, forte: true },
          { key: "clienti-wave", label: "Clienti unici in wave", valore: w.clienti_wave, colore: "#64748B" },
          { key: "wave-chiamati", label: "Clienti wave chiamati", valore: w.chiamati, perc: w.perc_chiamati, colore: "#64748B" },
          { key: "wave-raggiunti", label: "Clienti wave raggiunti", valore: w.raggiunti, perc: w.perc_raggiunti, colore: C.risposte },
        ]} />
        <p className="text-xs text-slate-500">
          "In wave del mese" = cliente presente nelle liste wave del CRM nello stesso mese della telefonata. I clienti unici del periodo sono contati
          una sola volta anche se presenti in più mesi; "raggiunti" = almeno una chiamata con risposta.
        </p>
      </div>
      {dettaglio && (
        <TabellaFasce testId="uscita-fasce" titolo={`Fasce orarie · ${meseEsteso(mese)}`} righe={dettaglio.fasce} onChiudi={() => toggle(mese)}
          colonne={[{ key: "effettuate", label: "Effettuate" }, { key: "risposte", label: "Risposte", classe: "text-emerald-700" },
            { key: "perc_risposte", label: "% risposte", perc: true, classe: "font-semibold text-[#0018A8]" }]} />
      )}
    </section>
  );
}

function SezioneEntrata({ entrata }) {
  const [mese, toggle] = useMeseSelezionato();
  const t = entrata.totale;
  const dettaglio = mese && entrata.mesi.find((m) => m.nome === mese);
  const tooltip = ({ active, label, payload }) => {
    const p = payload?.[0]?.payload;
    const su = (v) => `${pct(p.ricevute ? Math.round(v / p.ricevute * 1000) / 10 : null)} delle ricevute`;
    return p && <TooltipRighe active={active} label={label} righe={[
      [C.effettuate, "Ricevute", p.ricevute],
      [C.risposte, "Risposte", p.risposte, su(p.risposte)],
      [C.nonRisposte, "Non risposte", p.non_risposte, su(p.non_risposte)],
      ...(p.senza_esito ? [[C.senzaEsito, "Senza esito registrato", p.senza_esito, su(p.senza_esito)]] : []),
      ["#64748B", "Clienti unici non risposti", p.clienti_persi, su(p.clienti_persi)],
      ...SERIE_PERSE.map(([k, nome, col]) => [col, nome, p[k], `${pct(p[`perc_${k}`])} dei clienti`])]} />;
  };
  const vociPerse = SERIE_PERSE.map(([k, label, colore]) => ({ key: k, label, valore: t[k], perc: t[`perc_${k}`], colore, forte: k === "mai_risentiti" }));
  return (
    <section className="space-y-4" data-testid="telefonate-entrata">
      <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
        <PhoneIncoming size={18} className="text-emerald-700" /> Chiamate in entrata · centralino
      </h2>
      <div className="space-y-4">
        <Card className="p-4 space-y-3">
          <Legenda testId="entrata-legenda" voci={[[C.nonRisposte, "Non risposte"], [C.risposte, "Risposte"], [C.senzaEsito, "Senza esito"],
            ...SERIE_PERSE.map(([, nome, col]) => [col, nome])]} />
          <IstogrammaMensile testId="entrata-istogramma" dati={entrata.mesi} meseSelezionato={mese} onMese={toggle} tooltip={tooltip}
            etichetta="Barra sinistra: chiamate ricevute · barra destra: clienti unici con chiamata non risposta e cosa è successo · clicca per le fasce orarie"
            serie={[{ key: "non_risposte", nome: "Non risposte", colore: C.nonRisposte, stack: "ricevute" },
              { key: "risposte", nome: "Risposte", colore: C.risposte, stack: "ricevute" },
              { key: "senza_esito", nome: "Senza esito", colore: C.senzaEsito, stack: "ricevute", etichetta: "ricevute" },
              { key: "richiamati_risposta", nome: "Richiamati con risposta", colore: C.richRisposta, stack: "perse" },
              { key: "richiamanti_risposto", nome: "Richiamanti/risposti", colore: C.richiamanti, stack: "perse" },
              { key: "richiamati_senza_risposta", nome: "Richiamati senza risposta", colore: C.richSenza, stack: "perse" },
              { key: "mai_risentiti", nome: "Mai più sentiti", colore: C.mai, stack: "perse", etichetta: "clienti_persi" }]} />
        </Card>
        <BoxPeriodo testId="entrata-box" titolo="Totale periodo" voci={[
          { key: "ricevute", label: "Chiamate ricevute", valore: t.ricevute, colore: C.effettuate },
          { key: "risposte", label: "Risposte", valore: t.risposte, perc: t.ricevute ? Math.round(t.risposte / t.ricevute * 1000) / 10 : null, colore: C.risposte },
          { key: "non-risposte", label: "Non risposte", valore: t.non_risposte, perc: t.ricevute ? Math.round(t.non_risposte / t.ricevute * 1000) / 10 : null, colore: C.nonRisposte },
          ...(t.senza_esito ? [{ key: "senza-esito", label: "Senza esito", valore: t.senza_esito, perc: Math.round(t.senza_esito / t.ricevute * 1000) / 10, colore: C.senzaEsito }] : []),
          { key: "clienti-persi", label: "Clienti unici non risposti", valore: t.clienti_persi, perc: t.perc_clienti_persi, colore: "#64748B" },
          ...vociPerse,
        ]} />
      </div>
      <p className="text-xs text-slate-500">
        Il centralino registra le chiamate in entrata nel CRM da <strong>luglio 2026</strong>: prima non sono disponibili.
        Le non risposte sono ricondotte al <strong>cliente unico</strong> (chi chiama due volte senza risposta conta una volta sola) e classificate
        guardando cosa succede entro le 18:30 del primo giorno lavorativo successivo: richiamato da noi (con o senza risposta), ha richiamato e ha trovato
        risposta, oppure <strong>mai più sentito</strong>. Le percentuali degli esiti sono sui clienti unici non risposti; quelle di risposte/non risposte sulle chiamate ricevute.
      </p>
      {dettaglio && (
        <TabellaFasce testId="entrata-fasce" titolo={`Fasce orarie · ${meseEsteso(mese)}`} righe={dettaglio.fasce} onChiudi={() => toggle(mese)}
          colonne={[{ key: "ricevute", label: "Ricevute" }, { key: "risposte", label: "Risposte", classe: "text-emerald-700" },
            { key: "non_risposte", label: "Non risposte", classe: "text-red-700" },
            { key: "clienti_persi", label: "Clienti unici" },
            { key: "richiamati_risposta", label: "Richiamati ok", percDi: "perc_richiamati_risposta" },
            { key: "richiamanti_risposto", label: "Richiamanti/risposti", percDi: "perc_richiamanti_risposto" },
            { key: "richiamati_senza_risposta", label: "Richiamati no", percDi: "perc_richiamati_senza_risposta" },
            { key: "mai_risentiti", label: "Mai più sentiti", percDi: "perc_mai_risentiti", classe: "font-semibold text-red-900" }]} />
      )}
    </section>
  );
}

export default function AnalisiTelefonate() {
  const anno = 2026;
  const [filtri, setFiltri] = useState(VUOTO);
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");
  const set = (k, v) => setFiltri((f) => ({ ...f, [k]: v || "" }));
  const query = useMemo(() => {
    const p = new URLSearchParams({ anno });
    Object.entries(filtri).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [filtri, anno]);

  const carica = () => api.get(`/corto/telefonate?${query}`).then((r) => setDati(r.data)).catch(() => setErrore("Dati non disponibili"));
  useEffect(() => { setErrore(""); carica(); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps
  const inCorso = !!dati?.sync?.in_corso;
  useEffect(() => {
    if (!inCorso) return undefined;
    const t = setInterval(carica, 30000);
    return () => clearInterval(t);
  }, [inCorso]); // eslint-disable-line react-hooks/exhaustive-deps
  const [avvio, setAvvio] = useState(false);
  const rileggi = async () => {
    setAvvio(true);
    try {
      const r = await api.post("/corto/redemption-crm/aggiorna");
      toast.success(r.data.gia_in_corso ? "Lettura già in corso" : "Lettura telefonate dal CRM avviata");
      setTimeout(carica, 3000);
    } catch (e) { toast.error(apiError(e)); } finally { setAvvio(false); }
  };

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="telefonate-errore">{errore}</Card>;
  if (!dati) return <div className="text-sm text-slate-500" data-testid="telefonate-loading">Caricamento…</div>;

  return (
    <div className="space-y-8" data-testid="analisi-telefonate-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionTitle sub={`Telefonate CRM Databeasy · ${filtri.agenzia || "Totale DBE"} · dal ${dateIt(dati.dal)} al ${dateIt(dati.al)}${dati.sync?.ultimo_giorno ? ` · storico letto fino al ${dateIt(dati.sync.ultimo_giorno)}` : ""}${inCorso ? ` · lettura in corso (${dati.sync.fase || "…"})` : ""}`}>
          Analisi Telefonate
        </SectionTitle>
        <button type="button" onClick={rileggi} disabled={avvio || inCorso} data-testid="telefonate-rileggi-crm"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border-2 border-[#0018A8] text-[#0018A8] text-sm font-medium hover:bg-[#0018A8] hover:text-white transition-colors duration-200 disabled:opacity-50">
          <RefreshCw size={14} className={avvio || inCorso ? "animate-spin" : ""} /> {inCorso ? "Lettura in corso…" : "Rileggi telefonate dal CRM"}
        </button>
      </div>
      <Filtri filtri={filtri} set={set} azzera={() => setFiltri({ ...VUOTO, solo_wave: filtri.solo_wave })} agenzie={dati.agenzie_disponibili} operatori={dati.operatori_disponibili} />
      <SezioneUscita uscita={dati.uscita} wave={dati.wave} soloWave={!!filtri.solo_wave} setSoloWave={(v) => set("solo_wave", v ? "true" : "")} />
      <SezioneEntrata entrata={dati.entrata} />
      <RedemptionCrm anno={anno} agenzia={filtri.agenzia} />
      <ChiamateIngresso anno={anno} agenzia={filtri.agenzia} testId="telefonate-chiamate-ingresso" />
    </div>
  );
}
